/* eslint-disable max-lines-per-function */
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity } from '@adornis/baseql/decorators.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { parseBoolean, parseDate } from '@adornis/import-export/db/default-parse-functions.js';
import { serializeDate } from '@adornis/import-export/db/default-serialize-functions.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { SchoolSubject, Weekday } from '@chancenwerk/enums/enums.js';
import * as isoCountry from '@hotosm/iso-countries-languages';
import { DateTime, Settings } from 'luxon';
import { basename } from 'path';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { BuT, type DayAvailable, Student } from '../master-data-management/users/entities/Student.js';
import { BUT_SCHOOL_YEAR_PREFIX, currentSchoolYearLabel } from '../school-year.js';

Settings.defaultLocale = 'de';

const htmlCleanPattern = /(<\/?p>)|(<\/?br>)|\n/g;
const countryNameCache = new Map<string, string>();
const weekdayValues = Object.values(Weekday);

function cleanHTMLFieldForExport(htmlContent?: string) {
  if (!htmlContent) return '';
  return htmlContent
    .replaceAll(htmlCleanPattern, ' ')
    .replaceAll(/&gt;/g, '>')
    .replaceAll(/&nbsp;/g, ' ')
    .trim();
}

const emptyBuT = new BuT({}).toObject();

function getActiveBuTFromStudent(student: PartialEntityData<Student>, butYear: string): PartialEntityData<BuT> {
  for (const but of student.buts ?? []) {
    if (but.year === butYear) return but;
  }
  return emptyBuT;
}

const activeFieldBut = (field: string, butYear: string, parsingFunction?: any) => {
  const butCache = new WeakMap<PartialEntityData<Student>, PartialEntityData<BuT>>();
  return (value: any | undefined, data: PartialEntityData<Student>, i: number) => {
    let activeBuT = butCache.get(data);
    if (!activeBuT) {
      activeBuT = getActiveBuTFromStudent(data, butYear);
      butCache.set(data, activeBuT);
    }
    const valueFromBuT = activeBuT[field];
    if (parsingFunction) return parsingFunction(valueFromBuT);
    return valueFromBuT;
  };
};

/**
 * The BuT school years the export produces columns for: always the one that is currently running.
 *
 * Computed per export rather than once at module load, so a server that happens to be up across the 1. of
 * August starts writing the new year's columns without a restart. This used to be a hardcoded list that had
 * to be edited every summer.
 */
const exportedBuTYears = () => [currentSchoolYearLabel(BUT_SCHOOL_YEAR_PREFIX)];

@Entity()
export class StudentConvertConfig extends ConvertConfig<Student> {
  public static override _class = 'StudentConvertConfig';

  private static _butToFieldMappings(butKey: string, butYear: string, inputPrefix: string): FieldMappingEntry[] {
    return [
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Berechtigung` },
        undefined,
        activeFieldBut('BuTBerechtigung', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Unterlagen_Eltern` },
        wrappedParseDate(`${inputPrefix}Unterlagen_Eltern`),

        activeFieldBut('BuTUnterlagenEltern', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Grundantrag_erhalten` },
        wrappedParseDate(`${inputPrefix}Grundantrag_erhalten`),
        activeFieldBut('grundantragErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Grundantrag_Medium` },
        undefined,
        activeFieldBut('grundantragMedium', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Leistungsbescheid_erhalten` },
        wrappedParseDate(`${inputPrefix}Leistungsbescheid_erhalten`),
        activeFieldBut('leistungsbescheidErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Leistungsbescheid_Medium` },
        undefined,
        activeFieldBut('leistungsbescheidMedium', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Zeugnis_erhalten` },
        wrappedParseDate(`${inputPrefix}Zeugnis_erhalten`),
        activeFieldBut('zeugnisErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Zeugnis_Medium` },
        undefined,
        activeFieldBut('zeugnisMedium', butYear),
      ),
      // new FieldMappingEntry(
      //   { output: butKey, input: inputPrefix + 'Kind_nimmt_nicht_teil_Stand' },
      //   wrappedParseDate(inputPrefix + 'Kind_nimmt_nicht_teil_Stand'),
      //   activeFieldBut('kindNimmtNichtTeilStand', butYear, serializeDate()),
      // ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Verschwiegenheitsbogen_erhalten` },
        wrappedParseDate(`${inputPrefix}Verschwiegenheitsbogen_erhalten`),
        activeFieldBut('verschwiegenheitsbogenErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Verschwiegenheitsbogen_Medium` },
        undefined,
        activeFieldBut('verschwiegenheitsbogenMedium', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Unterlagen_vollstaendig` },
        wrappedParseDate(`${inputPrefix}BuT-Unterlagen_vollstaendig`),
        activeFieldBut('BuTUnterlagenVollstaendig', butYear, serializeDate()),
      ),
      // new FieldMappingEntry(
      //   { output: butKey, input: inputPrefix + 'Elternkontakt' },
      //   undefined,
      //   activeFieldBut('elternkontakt', butYear),
      // ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Elternkontakt_fehlgeschlagen` },
        wrappedParseDate(`${inputPrefix}Elternkontakt_fehlgeschlagen`),
        activeFieldBut('elternkontaktFehlgeschlagen', butYear, serializeDate()),
      ),
      // new FieldMappingEntry(
      //   {
      //     output: butKey,
      //     input: inputPrefix + 'Anlass_Kommentar_Elternkontakt',
      //   },
      //   undefined,
      //   activeFieldBut('anlassKommentarElternkontakt', butYear, text => {
      //     return !text ? '' : cleanHTMLFieldForExport(text);
      //   }),
      // ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Zusatzbogen_Schule_versendet` },
        wrappedParseDate(`${inputPrefix}Zusatzbogen_Schule_versendet`),
        activeFieldBut('zusatzbogenSchuleVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Zusatzbogen_Schule_erhalten` },
        wrappedParseDate(`${inputPrefix}Zusatzbogen_Schule_erhalten`),
        activeFieldBut('zusatzbogenSchuleErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Zusatzbogen_Schule_Medium` },
        undefined,
        activeFieldBut('zusatzbogenSchuleMedium', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Vereinbarungsnotitz_Schule` },
        undefined,
        activeFieldBut('vereinbarungsnotitzSchule', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Leistungstraeger` },
        undefined,
        activeFieldBut('leistungstraeger', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Antragsteller` },
        undefined,
        activeFieldBut('antragsteller', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Antrag_teilweise_versendet` },
        wrappedParseDate(`${inputPrefix}Antrag_teilweise_versendet`),
        activeFieldBut('antragTeilweiseVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Antrag_vollstaendig_versendet` },
        wrappedParseDate(`${inputPrefix}Antrag_vollstaendig_versendet`),
        activeFieldBut('antragVollstaendigVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Vereinbarungsnotiz_Amt` },
        undefined,
        activeFieldBut('vereinbarungsnotitzAmt', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Aktenzeichen` },
        undefined,
        activeFieldBut('aktenzeichen', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Bewilligung_ab` },
        wrappedParseDate(`${inputPrefix}Bewilligung_ab`),
        activeFieldBut('BuTBewilligungAb', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Bewilligung_bis` },
        wrappedParseDate(`${inputPrefix}Bewilligung_bis`),
        activeFieldBut('BuTBewilligungBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Kommentar Zeugnis` },
        undefined,
        activeFieldBut('kommentarBuT', butYear, text => {
          return !text ? '' : cleanHTMLFieldForExport(text);
        }),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Kommentar/Info` },
        undefined,
        activeFieldBut('anlassKommentarBuTSchuko', butYear, text => {
          return !text ? '' : cleanHTMLFieldForExport(text);
        }),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Kommentar_Weiterbewilligung` },
        undefined,
        activeFieldBut('kommentarWeiterbewilligung', butYear, text => {
          return text ? cleanHTMLFieldForExport(text) : '';
        }),
      ),

      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Massnahmen_einstellen_bis` },
        wrappedParseDate(`${inputPrefix}Massnahmen_einstellen_bis`),
        activeFieldBut('BuTMassnahmenEinstellenBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Leistungsbescheid_gueltig_bis` },
        wrappedParseDate(`${inputPrefix}Leistungsbescheid_gueltig_bis`),
        activeFieldBut('leistungsbescheidBerlinPassGueltigBis', butYear, serializeDate()),
      ),

      ...this._getFächer(inputPrefix, butYear),

      // new FieldMappingEntry(
      //   { output: butKey, input: inputPrefix + 'Zugewiesene_Person_Elternkontakt' },
      //   undefined,
      //   activeFieldBut('assignedPersonForParentalContact', butYear),
      // ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Kommentar_Abrechnung` },
        undefined,
        activeFieldBut('kommentarAbrechnung', butYear, cleanHTMLFieldForExport),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}Abgerechnet_bis` },
        wrappedParseDate(`${inputPrefix}Abgerechnet_bis`),
        activeFieldBut('abgerechnetBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: `${inputPrefix}berechtigt_durch` },
        undefined,
        activeFieldBut('BuTBerechtigungDurch', butYear),
      ),
    ];
  }

  private static _getFächer(inputPrefix: string, butYear: string) {
    const subjects = [SchoolSubject.DEUTSCH, SchoolSubject.ENGLISCH, SchoolSubject.MATHE];
    const subjectSet = new Set(subjects);
    const butCache = new WeakMap<PartialEntityData<Student>, PartialEntityData<BuT>>();
    const getBuT = (data: PartialEntityData<Student>) => {
      let but = butCache.get(data);
      if (!but) {
        but = getActiveBuTFromStudent(data, butYear);
        butCache.set(data, but);
      }
      return but;
    };
    return [
      ...subjects.flatMap(s => [
        new FieldMappingEntry({ output: null, input: `${inputPrefix}Bewilligt_${s}` }, undefined, (v, data, i) => {
          const but = getBuT(data);
          if (!but) return 'Nein';
          return (but.faecher ?? []).filter(f => f.subject === s).at(0)?.approved ? 'Ja' : 'Nein';
        }),
        new FieldMappingEntry(
          { output: null, input: `${inputPrefix}Bewilligte_Stunden_${s}` },
          undefined,
          (v, data, i) => {
            const but = getBuT(data);
            if (!but) return '';
            const result = ((but.faecher ?? []).filter(f => f.subject === s).at(0)?.bewilligteStunden ?? '').toString();
            return (result === '0' ? '' : result).replace('.', ',');
          },
        ),
        new FieldMappingEntry(
          { output: null, input: `${inputPrefix}Berechnete_Stunden_${s}` },
          undefined,
          (v, data, i) => {
            const but = getBuT(data);
            if (!but) return '';
            const result = ((but.faecher ?? []).filter(f => f.subject === s).at(0)?.berechneteStunden ?? '').toString();
            return (result === '0' ? '' : result).replace('.', ',');
          },
        ),
      ]),

      new FieldMappingEntry({ output: null, input: `${inputPrefix}Bewilligt_Freitext` }, undefined, (v, data, i) => {
        const but = getBuT(data);
        if (!but) return '';
        return (but.faecher ?? [])
          .filter(f => !subjectSet.has(f.subject))
          .map(f => f.subject)
          .join(', ');
      }),
      new FieldMappingEntry(
        { output: null, input: `${inputPrefix}Bewilligte_Stunden_Freitext` },
        undefined,
        (v, data, i) => {
          const but = getBuT(data);
          if (!but) return '';
          return (but.faecher ?? [])
            .filter(f => !subjectSet.has(f.subject))
            .map(f => f.bewilligteStunden ?? '0')
            .join(', ');
        },
      ),
      new FieldMappingEntry(
        { output: null, input: `${inputPrefix}Berechnete_Stunden_Freitext` },
        undefined,
        (v, data, i) => {
          const but = getBuT(data);
          if (!but) return '';
          return (but.faecher ?? [])
            .filter(f => !subjectSet.has(f.subject))
            .map(f => f.berechneteStunden ?? '0')
            .join(', ');
        },
      ),
    ];
  }

  private static _primitiveFieldMappings() {
    return [
      new FieldMappingEntry({ output: 'studentID', input: 'ID' }, (v, data, i) => '4900' + v),
      new FieldMappingEntry({ output: 'schuelertyp', input: 'Schuelertyp' }),
      new FieldMappingEntry({ output: 'vorname', input: 'Vorname' }),
      new FieldMappingEntry({ output: 'nachname', input: 'Familienname' }),
      new FieldMappingEntry(
        { output: 'geburtstag', input: 'Person_Geb' },
        wrappedParseDate('Person_Geb'),
        serializeDate(),
      ),
      new FieldMappingEntry({ output: 'geschlecht', input: 'Person_Geschlecht' }),
      new FieldMappingEntry({ output: 'handynummer', input: 'Person_Handy' }),
      new FieldMappingEntry({ output: 'staatsangehoerigkeit', input: 'Herkunft' }, undefined, v => {
        let result = countryNameCache.get(v);
        if (result === undefined) {
          result = isoCountry.default.getCountry('de', v);
          if (!result) result = v;
          countryNameCache.set(v, result ?? '');
        }
        return result ?? '';
      }),
      new FieldMappingEntry({ output: 'email', input: 'Person_Mail' }),
    ];
  }

  private static _complexFieldMappings() {
    return [
      new FieldMappingEntry({ output: 'adresse.strasse', input: 'STR' }),
      new FieldMappingEntry({ output: 'adresse.zipCity.zip', input: 'PLZ' }),
      new FieldMappingEntry({ output: 'adresse.zipCity.city', input: 'Stadt' }),
      new FieldMappingEntry({ output: 'bankInformation.kontoinhaber', input: 'Kontoinhaber' }),
      new FieldMappingEntry({ output: 'bankInformation.iban', input: 'IBAN' }),
      new FieldMappingEntry({ output: 'bankInformation.bic', input: 'BIC' }),
      new FieldMappingEntry({ output: 'bankInformation.kreditinstitut', input: 'Kreditinstitut' }),
      new FieldMappingEntry({ output: 'bankInformation.ibanFalsch', input: 'IBAN_Falsch' }),
      new FieldMappingEntry({ output: 'bankInformation.vermerkeZahlungsverkehr', input: 'Vermerke_Zahlungsverkehr' }),
      new FieldMappingEntry({ output: 'bankInformation.debitorennummer', input: 'Debitorennummer' }),
      new FieldMappingEntry(
        { output: 'bankInformation.mandatsdatum', input: 'Mandatsdatum' },
        wrappedParseDate('Mandatsdatum'),
        serializeDate(),
      ),
      new FieldMappingEntry({ output: 'bankInformation.mandatsreferenz', input: 'Mandatsreferenz' }),
      new FieldMappingEntry({ output: 'bankInformation.zahlungsart', input: 'Zahlungsart' }),
      new FieldMappingEntry(
        { output: 'bankInformation.zahlungAusgesetztAb', input: 'Zahlung_ausgesetzt_ab' },
        wrappedParseDate('Zahlung_ausgesetzt_ab'),
        serializeDate(),
      ),
      new FieldMappingEntry(
        { output: 'bankInformation.zahlungAusgesetztBis', input: 'Zahlung_ausgesetzt_bis' },
        wrappedParseDate('Zahlung_ausgesetzt_bis'),
        serializeDate(),
      ),
      new FieldMappingEntry(
        { output: 'bankInformation.mitgliedsbeitrag', input: 'Abweichender_Mitgliedsbeitrag' },
        (v, data, i) => {
          if (v === undefined) return 20;

          const parsedVal = parseInt(v, 10);
          if (Number.isNaN(parsedVal)) {
            const errorMsg = `Invalider Abweichender Mitgliedsbeitrag: ${v} - bankInformation.mitgliedsbeitrag`;
            (data as any).importLogs = `${(data as any).importLogs || ''}${errorMsg}<br />`;
            return 20;
          }
          if (parsedVal === 0) return 0;
          return parsedVal;
        },
        (v, data, i) => {
          if (v === 0) return '0';
          else if (v) return v.toString();
          return '';
        },
      ),
      new FieldMappingEntry(
        { output: 'bankInformation.membershipFeeLowerThan20', input: 'Mitgliedsbeitrag_unter_20' },
        wrappedParseBoolean('Mitgliedsbeitrag_unter_20'),
        (v, data, i) => (v === undefined ? '' : v.toString()),
      ),
    ];
  }

  private static _schoolInformationMappings() {
    return [
      new FieldMappingEntry(
        { output: 'roles', input: 'Schule' },
        undefined,
        async (v, data: PartialEntityData<Student>, i) => {
          const kpfs = (data.kpfs ?? []) as { school?: { kuerzel?: string }; produkt?: { kuerzel?: string } }[];
          if (!kpfs.length) return '';
          const kuerzels = new Set<string>();
          for (const kpf of kpfs) {
            const kuerzel = `${kpf?.school?.kuerzel ?? 'X_X'}_${kpf?.produkt?.kuerzel ?? 'X'}`;
            kuerzels.add(kuerzel);
          }
          return Array.from(kuerzels).join(' ');
        },
      ),
      new FieldMappingEntry({ output: 'schoolInformation.klassenlehrer', input: 'Klassenlehrer' }),
      new FieldMappingEntry({ output: 'schoolInformation.zug', input: 'Zug' }),
      new FieldMappingEntry(
        { output: 'schoolInformation.jahrgangsstufe', input: 'Jahrgangsstufe' },
        (v, data, i, entity: PartialEntityData<Student>) => {
          if (!v) return undefined;
          const zug = Number(v);
          if (Number.isNaN(zug)) {
            const errorMsg = `Invalide Jahrgangsstufe: '${v}' - Jahrgangsstufe `;
            entity.importLogs = `${entity.importLogs || ''}${errorMsg}<br />`;
            return undefined;
          }
          return zug;
        },
      ), // remove letters
      new FieldMappingEntry(
        { output: 'schoolInformation.abweichendeSchuleID', input: 'AbweichendeSchule' },
        undefined,
        async (id, data: PartialEntityData<Student>) => {
          return data.schoolInformation?.abweichendeSchuleResolved?.name ?? '';
        },
      ),
    ];
  }

  private static _legalGuardianMappings() {
    return [
      new FieldMappingEntry({ output: 'legalGuardian1.vorname', input: 'Erzb_1_Vorname' }),
      new FieldMappingEntry({ output: 'legalGuardian1.nachname', input: 'Erzb_1_Familienname' }),
      new FieldMappingEntry(
        { output: 'legalGuardian1.staatsangehoerigkeit', input: 'Erzb_1_Herkunft' },
        undefined,
        v => {
          let result = countryNameCache.get(v);
          if (result === undefined) {
            result = isoCountry.default.getCountry('de', v);
            if (!result) result = v;
            countryNameCache.set(v, result ?? '');
          }
          return result ?? '';
        },
      ),
      new FieldMappingEntry({ output: 'legalGuardian1.beruf', input: 'Erzb_1_Beruf' }),
      new FieldMappingEntry({ output: 'legalGuardian1.handynummer', input: 'Erzb_1_Mobil' }),
      new FieldMappingEntry({ output: 'legalGuardian1.festnetznummer', input: 'Person_Telefon' }),
      new FieldMappingEntry({ output: 'legalGuardian1.email', input: 'Erzb_1_Mail' }),
      new FieldMappingEntry({ output: 'legalGuardian1.geschlecht', input: 'Erzb_1_Sex' }),
      new FieldMappingEntry({ output: 'legalGuardian2.vorname', input: 'Erzb_2_Vorname' }),
      new FieldMappingEntry({ output: 'legalGuardian2.nachname', input: 'Erzb_2_Familienname' }),
      new FieldMappingEntry(
        { output: 'legalGuardian2.staatsangehoerigkeit', input: 'Erzb_2_Herkunft' },
        undefined,
        v => {
          let result = countryNameCache.get(v);
          if (result === undefined) {
            result = isoCountry.default.getCountry('de', v);
            if (!result) result = v;
            countryNameCache.set(v, result ?? '');
          }
          return result ?? '';
        },
      ),
      new FieldMappingEntry({ output: 'legalGuardian2.beruf', input: 'Erzb_2_Beruf' }),
      new FieldMappingEntry({ output: 'legalGuardian2.handynummer', input: 'Erzb_2_Mobil' }),
      new FieldMappingEntry({ output: 'legalGuardian2.email', input: 'Erzb_2_Mail' }),
      new FieldMappingEntry({ output: 'legalGuardian2.geschlecht', input: 'Erzb_2_Sex' }),
      new FieldMappingEntry(
        { output: 'legalGuardianContactMethods', input: 'Bevorzugte Kontaktwege' },
        undefined,
        (value, data, i) => {
          return value.join(', ');
        },
      ),
    ];
  }

  private static _consentMappings() {
    return [
      new FieldMappingEntry(
        { output: 'consent.einverstaendnisBild', input: 'Einverstaendnis_Bild' },
        wrappedParseBoolean('Einverstaendnis_Bild'),
      ),
      new FieldMappingEntry(
        {
          output: 'consent.einverstaendnisEntbindungVonVerschwiegenheit',
          input: 'Einverstaendnis_Entbindung_Von_Verschwiegenheit',
        },
        wrappedParseBoolean('Einverstaendnis_Entbindung_Von_Verschwiegenheit'),
      ),
      new FieldMappingEntry(
        { output: 'consent.einverstaendnisEvaluation', input: 'Einverstaendnis_Evaluation' },
        wrappedParseBoolean('Einverstaendnis_Evaluation'),
      ),
      new FieldMappingEntry(
        { output: 'consent.gotSignedInfopostBack', input: 'Verwaltung_Infobrief_zurueckerhalten' },
        wrappedParseDate('Verwaltung_Infobrief_zurueckerhalten'),
        serializeDate(),
      ),
    ];
  }

  private static _kuendingungMappings() {
    return [
      new FieldMappingEntry(
        { output: 'kuendigung.austrittsdatum', input: 'Austrittsdatum' },
        wrappedParseDate('Austrittsdatum'),
        serializeDate(),
      ),
      new FieldMappingEntry({ output: 'kuendigung.kuendigungGrund', input: 'Kuendigung_Grund' }),
      new FieldMappingEntry({ output: 'kuendigung.kuendigungGrundSonst', input: 'Kuendigung_Grund_Sonst' }),
      new FieldMappingEntry({ output: 'kuendigung.kuendigungArt', input: 'Kuendigung_art' }),
      new FieldMappingEntry({ output: 'kuendigung.kuendigungDurch', input: 'Kuendigung_durch' }),
      new FieldMappingEntry({ output: null, input: 'Kuendigung_Grund' }, undefined, (v, data, i) => {
        const student = data as Student;
        return student.kuendigung?.kuendigungGrund ?? '';
      }),
      new FieldMappingEntry({ output: null, input: 'Kuendigung_Grund_Sonst' }, undefined, (v, data, i) => {
        const student = data as Student;
        return student.kuendigung?.kuendigungGrundSonst ?? '';
      }),
    ];
  }

  private static _fundingMappings() {
    return [
      new FieldMappingEntry({ output: 'funding.teilleistungsschwaechen', input: 'Teilleistungsschwaechen' }),
      new FieldMappingEntry({ output: 'funding.teilleistungsschwaechenSonst', input: 'Teilleistungsschwaechen_Sonst' }),
      new FieldMappingEntry({ output: 'funding.vermerkeSonst', input: 'Vermerke_Sonst' }),
      new FieldMappingEntry(
        { output: 'funding.foerderbedarfLautEltern', input: 'Foerderbedarf_lt_Eltern' },
        (v: string | undefined, data, i, entity) => {
          if (!v) return [];
          const faecher = v.split(' ');
          const student = entity as Student;

          const otherFaecher = faecher
            .filter(f => !Object.values(SchoolSubject).includes(f as SchoolSubject))
            .join(', ');
          if (otherFaecher.length > 0) {
            const errorMsg = `Folgende Fächer konntent nicht importiert werden: "${otherFaecher}" - Foerderbedarf_lt_Eltern`;
            // @ts-expect-error
            student.importLogs = `${student.importLogs || ''}${errorMsg}<br />`;
          }

          return faecher.filter(fach => Object.values(SchoolSubject).includes(fach as SchoolSubject));
        },
      ),
    ];
  }

  private static _campusPageMetrics(pageloadeventlogMap: Promise<Map<string, { createdAt: Date }[]>>) {
    const relevantProducts = ['CHF', 'CAM'];
    const activityMetricCache = new WeakMap<PartialEntityData<Student>, { included: boolean; dates: Date[] | null }>();
    const getActivityMetricData = (data: PartialEntityData<Student>) => {
      let cached = activityMetricCache.get(data);
      if (cached) return cached;
      const kpfs = (data.kpfs ?? []) as { school?: { kuerzel?: string }; produkt?: { kuerzel?: string } }[];
      const isIncluded = kpfs.some(kpf => {
        const kuerzel = `${kpf?.school?.kuerzel ?? 'X_X'}_${kpf?.produkt?.kuerzel ?? 'X'}`;
        return relevantProducts.some(p => kuerzel.endsWith(p));
      });
      let dates: Date[] | null = null;
      if (isIncluded) {
        const analytic = data.activityAnalytics as Maybe<{ serviceLogins: { service: string; loginDates: Date[] }[] }>;
        if (analytic) {
          const sl = analytic.serviceLogins.find(s => s.service.toLowerCase() === 'chancencampus');
          dates = sl?.loginDates ?? null;
        }
      }
      cached = { included: isIncluded, dates };
      activityMetricCache.set(data, cached);
      return cached;
    };
    return [
      new FieldMappingEntry(
        { output: null, input: 'Letzter_Aufruf_CHANCENcampus' },
        undefined,
        async (value, data: PartialEntityData<Student>, i) => {
          if (!data._id) return '-';
          const { included, dates } = getActivityMetricData(data);
          if (!included) return '-';
          if (dates === null) {
            const logs = (await pageloadeventlogMap).get(data._id) ?? [];
            const latest = logs.map(a => a.createdAt).reduce((acc, elm) => (acc > elm ? acc : elm), new Date(0));
            if (!latest || latest.getTime() === 0) return '-';
            return DateTime.fromJSDate(latest).toFormat('dd.MM.yyyy') ?? '-';
          } else {
            const latest = dates.reduce((acc, elm) => (acc > elm ? acc : elm), new Date(0));
            if (!latest || latest.getTime() === 0) return '-';
            return DateTime.fromJSDate(latest).toFormat('dd.MM.yyyy') ?? '-';
          }
        },
      ),
      new FieldMappingEntry(
        { output: null, input: 'Anzahl_Aufrufe_CHANCENcampus' },
        undefined,
        async (value, data: PartialEntityData<Student>, i) => {
          if (!data._id) return '0';
          const { included, dates } = getActivityMetricData(data);
          if (!included) return '0';
          if (dates === null) {
            const logs = (await pageloadeventlogMap).get(data._id) ?? [];
            return logs.length.toString();
          } else {
            return (dates.length ?? 0).toString();
          }
        },
      ),
      new FieldMappingEntry(
        { output: 'lastCampusLogin', input: 'letzer Campus-Login' },
        wrappedParseDate('letzer Campus-Login'),
        serializeDate(),
      ),
    ];
  }

  private static _lernfoerdungMappings() {
    return [
      new FieldMappingEntry(
        { output: 'lernfoerderungDuerchfuehrungsTagChoosen', input: 'Lernfoerderung_Durchfuehrungstage' },
        undefined,
        async (value, data: PartialEntityData<Student>, i: number) => {
          const weekdays = weekdayValues;
          const days = (data.lernfoerderungDuerchfuehrungsTagChoosen ?? []) as {
            wochentag?: Weekday;
            startZeit?: DateTime;
            endZeit?: DateTime;
          }[];
          const sorted = days.toSorted((a, b) => {
            if (a.wochentag && b.wochentag) {
              return weekdays.indexOf(a.wochentag) - weekdays.indexOf(b.wochentag);
            } else if (!b.wochentag) {
              return 1;
            } else if (!a.wochentag) {
              return -1;
            }
            return 0;
          });
          return sorted
            .map(d => `${d.wochentag} ${d.startZeit?.toFormat('HH:mm') ?? ''} - ${d.endZeit?.toFormat('HH:mm') ?? ''}`)
            .join(', ');
        },
      ),
    ];
  }

  private static _preferredSubjectMappings() {
    return [
      new FieldMappingEntry({ output: 'preferedFirstSubject', input: 'Fach_Erstwunsch' }),
      new FieldMappingEntry({ output: 'preferedSecondSubject', input: 'Fach_Zweitwunsch' }),
      new FieldMappingEntry({ output: 'preferedThirdSubject', input: 'Fach_Drittwunsch' }),
    ];
  }

  private static _attendanceMappings() {
    return [
      new FieldMappingEntry(
        { output: null, input: 'Letzte_Anwesenheit' },
        undefined,
        async (value, data: PartialEntityData<Student>, i) => {
          return data.attendanceStats?.lastAttendance?.toFormat('dd.MM.yyyy') ?? '';
        },
      ),
      new FieldMappingEntry(
        { output: null, input: 'Anzahl_Anwesenheiten' },
        undefined,
        async (value, data: PartialEntityData<Student>, i) => {
          return data.attendanceStats?.attendanceCount?.toString() ?? '0';
        },
      ),
    ];
  }

  constructor() {
    const butYears = exportedBuTYears();
    const emptyMap = Promise.resolve(new Map());

    // Bulk-load all page load event logs grouped by userID
    const pageloadeventlogMap: Promise<Map<string, { createdAt: Date }[]>> = A.isServer
      ? getRawCollection<{ userID: string; createdAt: Date }>('eventlog').then(col =>
          col
            .find({ _class: 'PageLoadEventLog' }, { projection: { _id: 0, userID: 1, createdAt: 1 } })
            .toArray()
            .then(logs => {
              const map = new Map<string, { createdAt: Date }[]>();
              for (const log of logs) {
                if (!log.userID) continue;
                const arr = map.get(log.userID) ?? [];
                arr.push(log);
                map.set(log.userID, arr);
              }
              return map;
            }),
        )
      : emptyMap;

    // Bulk-load all non-archived digital courses
    type DigitalCourseData = {
      _id: string;
      subject?: string;
      weekday?: string;
      startTime?: Date;
      jahrgangsstufe?: number;
      osStudentIDs?: string[];
      usStudentIDs?: string[];
    };
    const digitalCoursesMap: Promise<Map<string, DigitalCourseData[]>> = A.isServer
      ? getRawCollection<DigitalCourseData>('courses').then(col =>
          col
            .find(
              { _class: 'CourseDigital', archivedAt: null },
              {
                projection: {
                  _id: 1,
                  subject: 1,
                  weekday: 1,
                  startTime: 1,
                  jahrgangsstufe: 1,
                  osStudentIDs: 1,
                  usStudentIDs: 1,
                },
              },
            )
            .toArray()
            .then(courses => {
              const map = new Map<string, typeof courses>();
              for (const course of courses) {
                for (const sid of [...(course.osStudentIDs ?? []), ...(course.usStudentIDs ?? [])]) {
                  const arr = map.get(sid) ?? [];
                  arr.push(course);
                  map.set(sid, arr);
                }
              }
              return map;
            }),
        )
      : emptyMap;

    // Bulk-load all student contact notes grouped by studentID
    type ContactNoteData = { studentID: string; contactUserID: string; date?: Date; note?: string; method?: string };
    const contactNotesMap: Promise<Map<string, ContactNoteData[]>> = A.isServer
      ? getRawCollection<ContactNoteData>('student_contact_notes').then(col =>
          col
            .find({}, { projection: { _id: 0, studentID: 1, contactUserID: 1, date: 1, note: 1, method: 1 } })
            .toArray()
            .then(notes => {
              const map = new Map<string, typeof notes>();
              for (const note of notes) {
                if (!note.studentID) continue;
                const arr = map.get(note.studentID) ?? [];
                arr.push(note);
                map.set(note.studentID, arr);
              }
              return map;
            }),
        )
      : emptyMap;

    // Bulk-load all contact users referenced in contact notes
    const contactUsersMap: Promise<Map<string, { vorname?: string; nachname?: string }>> = A.isServer
      ? contactNotesMap.then(async notesMap => {
          const allContactUserIDs = new Set<string>();
          for (const notes of notesMap.values()) {
            for (const note of notes) {
              if (note.contactUserID) allContactUserIDs.add(note.contactUserID);
            }
          }
          if (!allContactUserIDs.size) return new Map();
          const users = await getByID(
            ChancenwerkUser,
            Array.from(allContactUserIDs),
          )({ _id: 1, vorname: 1, nachname: 1 });
          return new Map(users.map(u => [u._id as string, { vorname: u.vorname, nachname: u.nachname }]));
        })
      : emptyMap;

    super({
      delimiter: ';',
      className: Student._class,
      uniqueIdentifierFieldName: 'studentID',
      fieldMappings: [
        ...StudentConvertConfig._primitiveFieldMappings(),
        ...StudentConvertConfig._complexFieldMappings(),
        ...butYears.flatMap(year =>
          StudentConvertConfig._butToFieldMappings('buts', year, `${year.replaceAll(' ', '_')}_`),
        ),

        ...StudentConvertConfig._schoolInformationMappings(),
        ...StudentConvertConfig._legalGuardianMappings(),
        ...StudentConvertConfig._consentMappings(),
        ...StudentConvertConfig._kuendingungMappings(),
        ...StudentConvertConfig._fundingMappings(),
        new FieldMappingEntry(
          { output: 'buts.0.sonstigeFaecherTmp', input: 'Foerderbedarf_Sonst' },
          (v, data, i) => v || '',
        ),
        new FieldMappingEntry(
          { output: 'joinInformation.eintrittsdatum', input: 'Eintrittsdatum' },
          wrappedParseDate('Eintrittsdatum'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'bildungsKarteNummer', input: 'Bildungskarten-Nummer' }),
        new FieldMappingEntry({ output: 'joinInformation.registermethode', input: 'Anmeldemethode' }),
        new FieldMappingEntry(
          { output: 'joinInformation.sendedPartTwoTillFive', input: 'Versand von Unterschriftenzettel an Eltern' },
          wrappedParseDate('Versand von Unterschriftenzettel an Eltern'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'zustellbesonderheiten', input: 'Anderer_Name_auf_Briefkasten' }),
        new FieldMappingEntry({ output: '_id', input: 'Digitale_Kurse_gewählt' }, undefined, async (text, data, i) => {
          const courses = (await digitalCoursesMap).get(text as string) ?? [];
          return courses
            .map(c =>
              `${c.subject ?? ''} ${c.weekday ?? ''} ${c.startTime ? DateTime.fromJSDate(c.startTime).toFormat('HH:mm') : ''} ${
                c.jahrgangsstufe ?? ''
              }`.trim(),
            )
            .join(', ');
        }),
        ...StudentConvertConfig._preferredSubjectMappings(),
        new FieldMappingEntry({ output: 'daysAvailable', input: 'Bedarfsabfrage' }, undefined, (value, data, i) => {
          const days = value as DayAvailable[];
          return days.map(d => `${d.day} (${d.timesAvailable.map(t => t.toFormat('HH:mm')).join(', ')})`).join(', ');
        }),
        new FieldMappingEntry(
          { output: 'bankInformation.wishesProofOfPayment', input: 'Nachweis_Beitragszahlungen_gewuenscht' },
          wrappedParseBoolean('Nachweis_Beitragszahlungen_gewuenscht'),
        ),
        new FieldMappingEntry({ output: 'guestAccount', input: 'ist_Gast' }, wrappedParseBoolean('ist_Gast')),

        new FieldMappingEntry({ output: '_id', input: 'Dokumentation' }, undefined, async (value, data, i) => {
          const docs = (await contactNotesMap).get(value as string) ?? [];
          const users = await contactUsersMap;

          return `"${docs
            .map(d => {
              const user = d.contactUserID ? users.get(d.contactUserID) : undefined;
              const dateStr = d.date ? DateTime.fromJSDate(d.date).toFormat('dd.MM.yyyy') : '';
              return `${dateStr},${user?.vorname ?? ''} ${user?.nachname ?? ''},${d.method ?? ''},${cleanHTMLFieldForExport(d.note ?? '')}`;
            })
            .join(';')}"`;
        }),
        ...StudentConvertConfig._lernfoerdungMappings(),
        ...StudentConvertConfig._campusPageMetrics(pageloadeventlogMap),
        ...StudentConvertConfig._attendanceMappings(),
        ...butYears.map(
          year =>
            new FieldMappingEntry(
              { output: 'buts', input: year.replaceAll(' ', '_') + '_Medium_Bewilligung' },
              undefined,
              activeFieldBut('bewilligungMedium', year),
            ),
        ),
      ],
      foreignKeys: [],
    });
  }
}

const wrappedParseDate = (header: string) => (value: string | undefined, data: Object, idx, entity) => {
  try {
    return parseDate(value);
  } catch (e) {
    const error = e as Error;
    const errorMsg = `${error.message}: "${value}" - ${header}`;
    entity.importLogs = `${entity.importLogs || ''}${errorMsg}<br />`;
    return null;
  }
};

const wrappedParseBoolean = (header: string) => (value: string | undefined, data: Object, idx, entity) => {
  try {
    return parseBoolean(false)(value);
  } catch (e) {
    const error = e as Error;
    const errorMsg = `${error.message}: "${value}" - ${header}`;
    entity.importLogs = (entity.importLogs || '') + `${errorMsg}<br />`;
    return null;
  }
};

export const exportStudents = registerMutation({
  operationName: 'exportStudents',
  type: () => String,
  params: [
    { type: () => StudentConvertConfig, name: 'convertConfig' },
    { type: () => String, name: 'filename' },
  ],
  resolve: (convertConfig: StudentConvertConfig, filename: string) => async () => {
    const constr = convertConfig.constr;
    const collection = await getRawCollection<EntityData<Student>>('adornisuser-materialized');
    const publishFilterQuery = await publishFilter(constr._class);
    let query: Record<string, unknown> = { _class: { $in: baseqlMetaData.entities.validSubclasses(Student._class) } };
    if (publishFilterQuery) query = { $and: [query, publishFilterQuery] };
    const cursor = collection.find(query).sort({ studentID: 1 });
    const path = await exportCSVFromEntities(convertConfig, cursor, filename);
    const exportedFileName = basename(path);
    return `/__export/download/${exportedFileName}`;
  },
});
